import type { PillarKey, PostStatus, SocialPlatform } from '../../core/types/social'

/**
 * Vaste keuzes van de social planner: kanalen, onderwerpen, statussen en het ritme.
 * Geen inhoud van Sarah — die staat in socialSeed.ts (en straks in de backend).
 */

// ——— Kanalen ———

export const platforms: SocialPlatform[] = ['Instagram-post', 'Instagram-story', 'Facebook', 'WhatsApp-status', 'Google-profiel']

type PlatformMeta = {
  /** 'feed' = 4:5 post, 'story' = 9:16 staand scherm. */
  format: 'feed' | 'story'
  /** Of er een tekst bij het bericht hoort. */
  caption: boolean
  captionMax: number
  hashtags: boolean
  /** Hoe een link werkt op dit kanaal. */
  link: 'bio' | 'sticker' | 'klikbaar'
  use: string
}

export const platformMeta: Record<SocialPlatform, PlatformMeta> = {
  'Instagram-post': {
    format: 'feed',
    caption: true,
    captionMax: 2200,
    hashtags: true,
    link: 'bio',
    use: '1080 × 1350 px. De eerste ± 125 tekens zie je direct, de rest onder “meer”.',
  },
  'Instagram-story': {
    format: 'story',
    caption: false,
    captionMax: 0,
    hashtags: false,
    link: 'sticker',
    use: '1080 × 1920 px, 24 uur zichtbaar. De tekst staat in het beeld; een link kan als sticker.',
  },
  Facebook: {
    format: 'feed',
    caption: true,
    captionMax: 5000,
    hashtags: false,
    link: 'klikbaar',
    use: 'Zelfde beeld als Instagram. Links zijn hier wél klikbaar; hashtags doen weinig.',
  },
  'WhatsApp-status': {
    format: 'story',
    caption: true,
    captionMax: 700,
    hashtags: false,
    link: 'klikbaar',
    use: 'Voor wie je nummer heeft: je vaste cliënten. 24 uur zichtbaar.',
  },
  'Google-profiel': {
    format: 'feed',
    caption: true,
    captionMax: 1500,
    hashtags: false,
    link: 'klikbaar',
    use: 'Een update op je Google-bedrijfsprofiel helpt mensen in de buurt je te vinden.',
  },
}

// ——— Onderwerpen ———

export const pillars: { key: PillarKey; label: string; text: string; dot: string }[] = [
  { key: 'kennis', label: 'Huidkennis', text: 'Wat je huid doet en waarom. Hier volgen ze je om.', dot: 'bg-sage' },
  { key: 'achter', label: 'Achter de schermen', text: 'Jij, de salon, je handen aan het werk. Bouwt vertrouwen.', dot: 'bg-muted' },
  { key: 'product', label: 'Product in de hand', text: 'Eén product, waarom je het kiest en hoe je het gebruikt.', dot: 'bg-clay' },
  { key: 'resultaat', label: 'Cliënt & resultaat', text: 'Een verhaal of resultaat — altijd met toestemming.', dot: 'bg-blush' },
  { key: 'aanbod', label: 'Salon & aanbod', text: 'Protocollen, behandelingen, cadeaubonnen. Af en toe.', dot: 'bg-ink' },
]

export const pillarByKey = (key: PillarKey) => pillars.find((p) => p.key === key) ?? pillars[0]

// ——— Status ———

export const statuses: { key: PostStatus; label: string; tone: 'neutral' | 'soft' | 'accent' | 'success' }[] = [
  { key: 'idee', label: 'Idee', tone: 'neutral' },
  { key: 'concept', label: 'Concept', tone: 'soft' },
  { key: 'klaar', label: 'Klaar om te posten', tone: 'accent' },
  { key: 'geplaatst', label: 'Geplaatst', tone: 'success' },
]

export const statusByKey = (key: PostStatus) => statuses.find((s) => s.key === key) ?? statuses[0]

/** Tijden waarop een doelgroep van 30–55 het meest kijkt: ’s avonds en in het weekend ’s ochtends. */
export const suggestedTimes = ['07:30', '12:15', '19:30', '20:30']

/** Het ritme uit Merkstijl: drie berichten per week. */
export const POSTS_PER_WEEK = 3

/** Instagram staat maximaal zoveel hashtags toe. */
export const MAX_HASHTAGS = 30

/** Woorden die niet bij Beauty Bewust passen. */
export const offBrandWords = ['korting', 'sale', 'uitverkoop', 'rimpels weg', 'gegarandeerd', 'wondermiddel', 'must-have']
